// Durable account sync. Anonymous visitors keep a complete local copy; after
// Sign in with ChatGPT, learning history, bookmarks, source sheets, and reader
// settings are merged into that user's D1-backed Mayanos account.
(function () {
  let account = { ready: false, authenticated: false, syncing: false, message: "Checking account…", stats: null };
  let timer = 0;
  let applying = false;
  const subscribers = new Set();

  function readBookmarks() {
    try {
      const parsed = JSON.parse(localStorage.getItem("mayanos-reader-bookmarks") || "[]");
      return Array.isArray(parsed) ? parsed.filter((ref) => typeof ref === "string" && ref).slice(0, 250) : [];
    } catch (error) { return []; }
  }

  function snapshot() {
    return {
      version: 2,
      workspace: window.WS ? window.WS.get() : null,
      bookmarks: readBookmarks(),
      history: window.MAYANOS_ACTIVITY ? window.MAYANOS_ACTIVITY.read() : [],
      settings: window.MAYANOS_SETTINGS_STORE ? window.MAYANOS_SETTINGS_STORE.read() : {},
      dictionary: window.MAYANOS_DICTIONARY ? window.MAYANOS_DICTIONARY.snapshot() : { history: [], saved: [] },
    };
  }

  function stateStats(state = snapshot()) {
    const workspace = state.workspace && Array.isArray(state.workspace.sheets) ? state.workspace : { sheets: [] };
    const history = Array.isArray(state.history) ? state.history : [];
    return {
      learning: history.length,
      completed: history.filter((item) => Number(item && item.progress) >= .98).length,
      bookmarks: Array.isArray(state.bookmarks) ? state.bookmarks.length : 0,
      sheets: workspace.sheets.filter((sheet) => sheet && (sheet.blocks || []).length > 0).length,
      lastRead: history[0] || null,
    };
  }

  const publish = (patch) => {
    const next = Object.assign({}, account, patch);
    if (!next.stats) next.stats = stateStats();
    account = next;
    window.MAYANOS_ACCOUNT = account;
    subscribers.forEach((subscriber) => subscriber(account));
    try { window.dispatchEvent(new CustomEvent("mayanos-account", { detail: account })); } catch (error) {}
  };

  function mergeHistory(localItems, remoteItems) {
    const merged = new Map();
    [...(remoteItems || []), ...(localItems || [])].forEach((item) => {
      if (!item || !item.ref) return;
      const previous = merged.get(item.ref);
      if (!previous) { merged.set(item.ref, item); return; }
      const latest = Number(item.updatedAt) >= Number(previous.updatedAt) ? item : previous;
      merged.set(item.ref, {
        ...previous,
        ...latest,
        progress: Math.max(Number(previous.progress) || 0, Number(item.progress) || 0),
        lastPosition: Math.max(Number(previous.lastPosition) || 0, Number(item.lastPosition) || 0),
      });
    });
    return [...merged.values()].sort((a, b) => Number(b.updatedAt) - Number(a.updatedAt)).slice(0, 250);
  }

  function mergeDictionary(local, remote) {
    const localValue = local || { history: [], saved: [] };
    const remoteValue = remote || { history: [], saved: [] };
    const history = new Map();
    [...(remoteValue.history || []), ...(localValue.history || [])].forEach((item) => {
      if (!item || !item.key) return;
      const previous = history.get(item.key);
      if (!previous || Number(item.searchedAt) >= Number(previous.searchedAt)) history.set(item.key, item);
    });
    const saved = new Map();
    [...(remoteValue.saved || []), ...(localValue.saved || [])].forEach((item) => {
      const key = String(item && (item.word || item.id) || "").trim().toLocaleLowerCase();
      if (key && !saved.has(key)) saved.set(key, item);
    });
    return { history: [...history.values()].sort((a, b) => Number(b.searchedAt) - Number(a.searchedAt)).slice(0, 100), saved: [...saved.values()].slice(0, 100) };
  }

  function workspaceHasContent(workspace) {
    return !!(workspace && (
      (Array.isArray(workspace.collected) && workspace.collected.length) ||
      (Array.isArray(workspace.folders) && workspace.folders.some((folder) => folder && Array.isArray(folder.items) && folder.items.length)) ||
      (Array.isArray(workspace.sheets) && workspace.sheets.some((sheet) => sheet && Array.isArray(sheet.blocks) && sheet.blocks.length))
    ));
  }

  function mergeWorkspace(local, remote) {
    if (!remote) return local;
    if (!local) return remote;
    if (!workspaceHasContent(local)) return remote;
    if (!workspaceHasContent(remote)) return local;
    const remoteSheets = Array.isArray(remote.sheets) ? remote.sheets : [];
    const ids = new Set(remoteSheets.map((sheet) => sheet && sheet.id));
    const localExtras = (Array.isArray(local.sheets) ? local.sheets : []).filter((sheet) => sheet && !ids.has(sheet.id) && (sheet.blocks || []).length);
    const folders = new Map();
    [...(remote.folders || []), ...(local.folders || [])].forEach((folder) => {
      if (!folder) return;
      const previous = folders.get(folder.id);
      folders.set(folder.id, previous
        ? { ...previous, ...folder, items: [...new Set([...(previous.items || []), ...(folder.items || [])].map(String))] }
        : { ...folder, items: [...new Set((folder.items || []).map(String))] });
    });
    return {
      ...remote,
      collected: [...new Set([...(remote.collected || []), ...(local.collected || [])])],
      folders: [...folders.values()],
      sourceCache: { ...(remote.sourceCache || {}), ...(local.sourceCache || {}) },
      sheets: remoteSheets.concat(localExtras),
      activeId: remote.activeId || local.activeId,
    };
  }

  function mergeState(local, remote) {
    return {
      version: 2,
      workspace: mergeWorkspace(local.workspace, remote && remote.workspace),
      bookmarks: [...new Set([...(remote && remote.bookmarks || []), ...(local.bookmarks || [])])].slice(0, 250),
      history: mergeHistory(local.history, remote && remote.history),
      settings: { ...(local.settings || {}), ...(remote && remote.settings || {}) },
      dictionary: mergeDictionary(local.dictionary, remote && remote.dictionary),
    };
  }

  function applyState(state) {
    applying = true;
    try {
      if (state.workspace && window.WS) window.WS.replace(state.workspace);
      if (Array.isArray(state.history) && window.MAYANOS_ACTIVITY) window.MAYANOS_ACTIVITY.replace(state.history);
      if (Array.isArray(state.bookmarks)) localStorage.setItem("mayanos-reader-bookmarks", JSON.stringify(state.bookmarks));
      if (state.settings && window.MAYANOS_SETTINGS_STORE) window.MAYANOS_SETTINGS_STORE.replace(state.settings);
      if (state.dictionary && window.MAYANOS_DICTIONARY) window.MAYANOS_DICTIONARY.replace(state.dictionary);
    } finally {
      applying = false;
    }
  }

  const save = async (state) => {
    if (!account.authenticated || applying) return false;
    const value = state || snapshot();
    publish({ syncing: true, message: "Saving your learning…", stats: stateStats(value) });
    try {
      const response = await fetch("/bff/user-state", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(value),
      });
      if (!response.ok) throw new Error("Account save failed");
      const result = await response.json();
      publish({ syncing: false, message: "Everything is saved", lastSyncedAt: result.updatedAt || Date.now(), stats: stateStats(value) });
      return true;
    } catch (error) {
      publish({ syncing: false, message: "Account sync is temporarily unavailable", stats: stateStats(value) });
      return false;
    }
  };

  const schedule = () => {
    if (!account.authenticated || applying) return;
    clearTimeout(timer);
    timer = setTimeout(() => save(), 900);
  };

  const init = async () => {
    try {
      const returnTo = location.pathname + location.search;
      const meResponse = await fetch(`/bff/me?returnTo=${encodeURIComponent(returnTo)}`, { cache: "no-store" });
      const me = await meResponse.json();
      if (!me.authenticated) {
        publish({
          ready: true,
          authenticated: false,
          signIn: me.signIn || "/signin-with-chatgpt",
          message: "Saved on this device",
          stats: stateStats(),
        });
        return;
      }
      publish({
        ready: true,
        authenticated: true,
        displayName: me.displayName || "Signed in",
        analyticsAdmin: !!me.analyticsAdmin,
        signOut: me.signOut || "/signout-with-chatgpt",
        message: "Loading your learning…",
        stats: stateStats(),
      });
      if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("account_signed_in", { analyticsAdmin: !!me.analyticsAdmin });
      const stateResponse = await fetch("/bff/user-state", { cache: "no-store" });
      const data = stateResponse.ok ? await stateResponse.json() : { state: null };
      const merged = mergeState(snapshot(), data.state || {});
      applyState(merged);
      publish({
        message: data.state ? "Your learning is synced" : "Creating your Mayanos account…",
        lastSyncedAt: data.updatedAt || null,
        stats: stateStats(merged),
      });
      await save(merged);
    } catch (error) {
      applying = false;
      publish({ ready: true, syncing: false, message: "Account sync is temporarily unavailable", stats: stateStats() });
    }
  };

  function useAccount() {
    const [value, setValue] = React.useState(account);
    React.useEffect(() => { subscribers.add(setValue); setValue(account); return () => subscribers.delete(setValue); }, []);
    return value;
  }

  window.MAYANOS_ACCOUNT_SYNC = { schedule, save, init, snapshot, stats: () => stateStats() };
  window.useMayanosAccount = useAccount;
  window.MAYANOS_ACCOUNT = account;
  setTimeout(init, 0);
})();
